/**
 * Wire-shape constants and type guards for host→plugin bridge push messages
 * (WI 4858 sub-plan 2). These types ride the existing MessagePort channel
 * alongside the `ethisys:mcp:*` and `ethisys:remotedom` envelopes — the
 * `type` discriminant keeps them fully separate at the dispatch site.
 *
 * The host posts bridge pushes through `WorkerRemoteDomTransport.hostPort`;
 * the plugin side reads them in `createPortBridgeClient` (plugin-ui package).
 * In-realm (Tier T) plugins use `InMemoryBridgeTransport` which calls the
 * subscriber callbacks directly — no serialisation needed.
 */

// ── Type constants ───────────────────────────────────────────────────────────

export const BRIDGE_PUSH_THEME       = "ethisys:bridge:theme"          as const;
export const BRIDGE_PUSH_LOCALE      = "ethisys:bridge:locale"         as const;
export const BRIDGE_PUSH_DENSITY     = "ethisys:bridge:density"        as const;
export const BRIDGE_PUSH_A11Y        = "ethisys:bridge:a11y"           as const;
export const BRIDGE_NAV_PUSH         = "ethisys:bridge:nav"            as const;
export const BRIDGE_CHROME_REQUEST   = "ethisys:bridge:chrome:request" as const;
export const BRIDGE_CHROME_RESPONSE  = "ethisys:bridge:chrome:response" as const;
export const BRIDGE_SESSION_TOKEN_PUSH = "ethisys:bridge:token"        as const;
export const BRIDGE_LIFECYCLE_EVENT  = "ethisys:bridge:lifecycle"      as const;
export const BRIDGE_A11Y_ANNOUNCE    = "ethisys:bridge:a11y:announce"  as const;

// ── Envelope shapes ──────────────────────────────────────────────────────────

/** Theme push: host → plugin whenever the host theme changes. */
export interface BridgePushThemeEnvelope {
  readonly type: typeof BRIDGE_PUSH_THEME;
  /** "light" | "dark" | "high-contrast" */
  readonly mode: string;
  /** Flat design-token map (CSS-variable-name → value). May be empty. */
  readonly tokens: Readonly<Record<string, string>>;
}

/** Locale push: host → plugin whenever the active locale changes. */
export interface BridgePushLocaleEnvelope {
  readonly type: typeof BRIDGE_PUSH_LOCALE;
  /** BCP 47 tag, e.g. "en-GB". */
  readonly locale: string;
  /** "ltr" | "rtl" */
  readonly dir: "ltr" | "rtl";
}

/** Density push: host → plugin when the UI density preference changes. */
export interface BridgePushDensityEnvelope {
  readonly type: typeof BRIDGE_PUSH_DENSITY;
  /** "comfortable" | "compact" */
  readonly density: string;
}

/** A11y push: host → plugin when accessibility prefs change. */
export interface BridgePushA11yEnvelope {
  readonly type: typeof BRIDGE_PUSH_A11Y;
  readonly reducedMotion: boolean;
  readonly highContrast: boolean;
}

/** Nav push: host → plugin when the SPA location changes. */
export interface BridgeNavPushEnvelope {
  readonly type: typeof BRIDGE_NAV_PUSH;
  /** Current SPA path (pathname + search). */
  readonly path: string;
  /** `window.history.length` at the time of push. */
  readonly historyLength: number;
}

/** Plugin → host: request a chrome action (e.g. toast, modal). */
export interface BridgeChromeRequestEnvelope {
  readonly type: typeof BRIDGE_CHROME_REQUEST;
  /** Correlation id — matched against the response. */
  readonly id: string;
  /** "toast" | "confirm" | "openUrl" */
  readonly action: string;
  readonly payload: Readonly<Record<string, unknown>>;
}

/** Host → plugin: response to a chrome request. */
export interface BridgeChromeResponseEnvelope {
  readonly type: typeof BRIDGE_CHROME_RESPONSE;
  readonly id: string;
  readonly ok: boolean;
  readonly result?: unknown;
  readonly error?: string;
}

/** Host → plugin: push a short-lived frontend-session token. */
export interface BridgeSessionTokenPushEnvelope {
  readonly type: typeof BRIDGE_SESSION_TOKEN_PUSH;
  /** Opaque JWT string — audience-restricted to the plugin's own backend. */
  readonly token: string;
  /** Absolute epoch-ms at which the token expires. Refresh fires 30 s before. */
  readonly expiresAtMs: number;
}

/** Host ↔ plugin lifecycle event. */
export type BridgeLifecyclePhase = "load" | "ready" | "error" | "unload";

export interface BridgeLifecycleEventEnvelope {
  readonly type: typeof BRIDGE_LIFECYCLE_EVENT;
  readonly phase: BridgeLifecyclePhase;
  /** Present when phase === "error". */
  readonly message?: string;
}

/** Plugin → host: a11y live-region announcement request. */
export interface BridgeA11yAnnounceEnvelope {
  readonly type: typeof BRIDGE_A11Y_ANNOUNCE;
  readonly message: string;
  /** "polite" | "assertive" */
  readonly politeness: "polite" | "assertive";
}

// ── Type guards ──────────────────────────────────────────────────────────────

function isObj(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object";
}

export function isBridgePushTheme(v: unknown): v is BridgePushThemeEnvelope {
  return isObj(v) && v["type"] === BRIDGE_PUSH_THEME && typeof v["mode"] === "string";
}

export function isBridgePushLocale(v: unknown): v is BridgePushLocaleEnvelope {
  return isObj(v) && v["type"] === BRIDGE_PUSH_LOCALE && typeof v["locale"] === "string";
}

export function isBridgePushDensity(v: unknown): v is BridgePushDensityEnvelope {
  return isObj(v) && v["type"] === BRIDGE_PUSH_DENSITY && typeof v["density"] === "string";
}

export function isBridgePushA11y(v: unknown): v is BridgePushA11yEnvelope {
  return isObj(v) && v["type"] === BRIDGE_PUSH_A11Y
    && typeof v["reducedMotion"] === "boolean"
    && typeof v["highContrast"] === "boolean";
}

export function isBridgeNavPush(v: unknown): v is BridgeNavPushEnvelope {
  return isObj(v) && v["type"] === BRIDGE_NAV_PUSH && typeof v["path"] === "string";
}

export function isBridgeChromeRequest(v: unknown): v is BridgeChromeRequestEnvelope {
  return isObj(v) && v["type"] === BRIDGE_CHROME_REQUEST && typeof v["id"] === "string";
}

export function isBridgeChromeResponse(v: unknown): v is BridgeChromeResponseEnvelope {
  return isObj(v) && v["type"] === BRIDGE_CHROME_RESPONSE && typeof v["id"] === "string";
}

export function isBridgeSessionTokenPush(v: unknown): v is BridgeSessionTokenPushEnvelope {
  return isObj(v) && v["type"] === BRIDGE_SESSION_TOKEN_PUSH
    && typeof v["token"] === "string"
    && typeof v["expiresAtMs"] === "number";
}

export function isBridgeLifecycleEvent(v: unknown): v is BridgeLifecycleEventEnvelope {
  return isObj(v) && v["type"] === BRIDGE_LIFECYCLE_EVENT && typeof v["phase"] === "string";
}

export function isBridgeA11yAnnounce(v: unknown): v is BridgeA11yAnnounceEnvelope {
  return isObj(v) && v["type"] === BRIDGE_A11Y_ANNOUNCE && typeof v["message"] === "string";
}
